import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { MaskedLine, scrollToId, EASE } from "./shared";

const STATS = [
  { value: "100–300 g", label: "dávka pražení" },
  { value: "15 min", label: "od zrnka k šálku" },
  { value: "1 tlačítko", label: "plně automaticky" },
];

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 130]);
  const yBg = useTransform(scrollY, [0, 900], [0, -80]);
  const opacityBg = useTransform(scrollY, [0, 600], [1, 0.2]);

  return (
    <section id="hero" className="relative overflow-hidden bg-white pt-[72px]" data-testid="hero-section">
      <motion.span
        style={{ y: yBg, opacity: opacityBg }}
        aria-hidden="true"
        className="text-outline pointer-events-none absolute -bottom-8 left-0 select-none whitespace-nowrap font-display text-[22vw] font-black uppercase leading-none"
      >
        Cube&nbsp;X
      </motion.span>

      <div className="relative mx-auto grid min-h-[calc(100vh-72px)] max-w-7xl grid-cols-1 items-center gap-12 px-6 py-16 lg:grid-cols-12 lg:py-0">
        <div className="relative z-10 lg:col-span-7">
          <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter text-stone-900 sm:text-6xl md:text-7xl">
            <MaskedLine delay={0.45}>Pražte vlastní</MaskedLine>
            <MaskedLine delay={0.58}>
              kávu. <span className="text-[#B08968]">Kdekoliv.</span>
            </MaskedLine>
            <MaskedLine delay={0.71}>Kdykoliv.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg"
            data-testid="hero-subtitle"
          >
            BeanGo Cube® X promění zelená zrnka v čerstvě praženou kávu. Nasypete,
            stisknete tlačítko — a za chvíli voní celý domov.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => scrollToId("#form")}
              className="group flex items-center gap-3 rounded-full bg-gold px-8 py-4 font-display text-base font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-gold-light"
              data-testid="hero-cta-primary"
            >
              Mám zájem o ukázku
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => scrollToId("#prazicka")}
              className="flex items-center gap-3 rounded-full border border-stone-300 px-8 py-4 font-display text-base font-bold uppercase tracking-wide text-stone-800 transition-colors duration-300 hover:border-gold/60 hover:text-gold"
              data-testid="hero-cta-secondary"
            >
              <Play className="h-4 w-4" />
              Prozkoumat pražičku
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.35 }}
            className="mt-14 flex gap-10 border-t border-stone-200 pt-8"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-2xl font-black text-stone-900 sm:text-3xl">{s.value}</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <div className="spotlight absolute -inset-16" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
            style={{ y: yImg }}
            className="relative"
          >
            <div className="group overflow-hidden rounded-lg border border-stone-200 shadow-sm" data-testid="hero-image-frame">
              <img
                src="/img/home.webp"
                alt="BeanGo Cube X v domácí kávové výbavě"
                className="w-full scale-[1.02] object-cover transition-transform duration-[1.8s] ease-out group-hover:scale-110"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 1.4, ease: EASE }}
              className="absolute -bottom-8 -left-4 flex items-center gap-3 rounded-lg border border-stone-200 bg-white/90 px-5 py-4 shadow-sm backdrop-blur-md sm:-left-10"
              data-testid="hero-award-chip"
            >
              <img src="/img/award-wcia.png" alt="World Coffee Innovation Awards 2025" className="h-12 w-12 object-contain" />
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-wider text-gold">WCIA 2025</p>
                <p className="text-[11px] text-stone-500">Vítěz — Roasting Innovation</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
